import React from "react";
import {
  X,
  ImageIcon,
  FileText,
  ListChecks,
  MapPin,
  Loader,
  Hash,
  BarChart2,
} from "lucide-react";
import PollCreator from "./poll-creator";
import styled from "styled-components";
import { LoggedInUserContext } from "../../providers/ProvideLoggedInUser";
import { getCsrfToken } from "../../util";
import { apiGet } from "../../api";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
`;

const Modal = styled.div`
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 1rem;
  width: 100%;
  max-width: 42rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid var(--border);
`;

const CloseButton = styled.button`
  border-radius: 9999px;
  height: 2rem;
  width: 2rem;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: rgba(255, 153, 0, 0.1);
  }
`;

const Title = styled.h2`
  font-size: 1.125rem;
  font-weight: bold;
  color: var(--foreground);
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  gap: 1rem;
`;

const Avatar = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 9999px;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AvatarText = styled.span`
  font-size: 0.875rem;
  font-weight: bold;
  color: var(--primary-foreground);
`;

const InputContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Textarea = styled.textarea`
  width: 100%;
  background: transparent;
  font-size: 1.125rem;
  font-weight: 500;
  color: var(--foreground);
  outline: none;
  resize: none;
  border: none;
  line-height: 1.6;
  font-family: inherit;

  &::placeholder {
    color: var(--muted-foreground);
  }
`;

const CharacterCount = styled.div`
  font-size: 0.875rem;
  color: ${(p) => (p.$warn ? "#ef4444" : "var(--muted-foreground)")};
`;

// Media previews
const MediaPreviewGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const ImagePreview = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid var(--border);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DocChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.75rem;
  background: var(--secondary);
  border-radius: 9999px;
  font-size: 0.8125rem;
  color: var(--foreground);
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RemoveFileBtn = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  line-height: 1;
`;

const LocationTag = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  color: var(--primary);
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const Footer = styled.div`
  border-top: 1px solid var(--border);
  padding: 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ToolsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ToolButton = styled.button`
  border-radius: 9999px;
  height: 2rem;
  width: 2rem;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) =>
    p.$active ? "rgba(var(--primary-rgb), 0.15)" : "transparent"};
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: rgba(255, 153, 0, 0.1);
  }

  svg {
    color: ${(p) => (p.$active ? "var(--primary)" : "var(--muted-foreground)")};
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Tip = styled.div`
  font-size: 0.75rem;
  color: var(--muted-foreground);
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Button = styled.button`
  padding: 0.5rem 1.5rem;
  border-radius: 9999px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.375rem;

  ${(props) =>
    props.$variant === "outline"
      ? `
    background: transparent;
    border: 1px solid var(--border);
    color: var(--foreground);

    &:hover {
      background: var(--secondary);
    }
  `
      : `
    background: var(--primary);
    color: var(--primary-foreground);

    &:hover {
      opacity: 0.9;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const MAX_CHARS = 5000;

export default function CreateThreadModal({ onClose, onCreated }) {
  const { currentUser } = React.use(LoggedInUserContext);
  const [content, setContent] = React.useState("");
  const [mediaFiles, setMediaFiles] = React.useState([]); // { file, url, type: 'image'|'doc' }
  const [checklistMode, setChecklistMode] = React.useState(false);
  const [locationTag, setLocationTag] = React.useState(null); // "📍 lat, lng"
  const [locLoading, setLocLoading] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [topics, setTopics] = React.useState([]);
  const [selectedTopics, setSelectedTopics] = React.useState([]);
  const [pollMode, setPollMode] = React.useState(false);
  const [pollData, setPollData] = React.useState(null);

  const imageInputRef = React.useRef();
  const docInputRef = React.useRef();

  React.useEffect(() => {
    apiGet("/api/v1/communities/sidebar").then((res) => {
      if (res.parsedResponse?.topics) setTopics(res.parsedResponse.topics);
    });
  }, []);

  const userInitial =
    currentUser?.name?.charAt(0) || currentUser?.username?.charAt(0) || "?";

  // When checklist mode is toggled on, prepend "- [ ] " to empty content
  const handleChecklistToggle = () => {
    setChecklistMode((prev) => {
      const next = !prev;
      if (next && !content.trim()) {
        setContent("- [ ] ");
      }
      return next;
    });
  };

  const handleContentChange = (e) => {
    const val = e.target.value;
    if (val.length > MAX_CHARS) return;
    setContent(val);
  };

  // In checklist mode, hitting Enter auto-inserts "- [ ] " prefix
  const handleKeyDown = (e) => {
    if (checklistMode && e.key === "Enter") {
      e.preventDefault();
      setContent((prev) => prev + "\n- [ ] ");
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      handleSubmit();
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const newItems = files.map((f) => ({
      file: f,
      url: URL.createObjectURL(f),
      type: "image",
    }));
    setMediaFiles((prev) => [...prev, ...newItems]);
    e.target.value = "";
  };

  const handleDocSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const newItems = files.map((f) => ({ file: f, url: null, type: "doc" }));
    setMediaFiles((prev) => [...prev, ...newItems]);
    e.target.value = "";
  };

  const removeMedia = (idx) => {
    setMediaFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleLocation = () => {
    if (locationTag) {
      setLocationTag(null);
      return;
    }
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser.");
      return;
    }
    setLocLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude.toFixed(4);
        const lng = pos.coords.longitude.toFixed(4);
        setLocationTag(`📍 ${lat}, ${lng}`);
        setLocLoading(false);
      },
      () => {
        setError("Could not get your location. Please allow location access.");
        setLocLoading(false);
      },
    );
  };

  const handlePollToggle = () => {
    setPollMode((prev) => {
      if (prev) setPollData(null);
      return !prev;
    });
  };

  const validPollOptions = pollData
    ? pollData.options.filter((o) => o.trim())
    : [];

  const handleSubmit = async () => {
    let finalContent = content.trim();
    if (!finalContent && mediaFiles.length === 0 && validPollOptions.length < 2)
      return;

    if (locationTag) {
      finalContent = finalContent
        ? `${finalContent}\n${locationTag}`
        : locationTag;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("content", finalContent);
      selectedTopics.forEach((t) => formData.append("topic_ids[]", t.id));
      mediaFiles.forEach((m) => formData.append("media_files[]", m.file));
      if (validPollOptions.length >= 2) {
        validPollOptions.forEach((o) => formData.append("poll[options][]", o));
        if (pollData.expires_at)
          formData.append("poll[expires_at]", pollData.expires_at);
      }

      const response = await fetch("/api/v1/communities/threads", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        body: formData,
      });

      const data = await response.json();
      if (response.status === 201) {
        onCreated?.(data.data);
        onClose();
      } else {
        setError(
          data?.errors?.join(", ") || "Failed to post. Please try again.",
        );
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const images = mediaFiles.filter((m) => m.type === "image");
  const docs = mediaFiles.filter((m) => m.type === "doc");

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <CloseButton onClick={onClose}>
            <X size={18} />
          </CloseButton>
          <Title>Compose new thread</Title>
          <div style={{ width: "2rem" }} />
        </Header>

        <ContentArea>
          <Avatar>
            <AvatarText>{userInitial}</AvatarText>
          </Avatar>

          <InputContainer>
            <Textarea
              value={content}
              onChange={handleContentChange}
              onKeyDown={handleKeyDown}
              placeholder={
                checklistMode
                  ? "- [ ] First item…"
                  : "What's happening in the tax world today?"
              }
              rows={6}
              autoFocus
            />

            {/* Topic selector */}
            {topics.length > 0 && (
              <TopicSelector>
                <TopicSelectorLabel>
                  <Hash size={14} />
                  Topics
                </TopicSelectorLabel>
                <TopicChips>
                  {topics.map((topic) => {
                    const isSelected = selectedTopics.some(
                      (t) => t.id === topic.id,
                    );
                    return (
                      <TopicChip
                        key={topic.id}
                        $selected={isSelected}
                        onClick={() => {
                          setSelectedTopics((prev) =>
                            isSelected
                              ? prev.filter((t) => t.id !== topic.id)
                              : [...prev, topic],
                          );
                        }}
                      >
                        {topic.name}
                      </TopicChip>
                    );
                  })}
                </TopicChips>
              </TopicSelector>
            )}

            {/* Media previews */}
            {(images.length > 0 || docs.length > 0) && (
              <MediaPreviewGrid>
                {mediaFiles.map((m, i) =>
                  m.type === "image" ? (
                    <ImagePreview key={m.url}>
                      <img src={m.url} alt="" />
                      <RemoveFileBtn onClick={() => removeMedia(i)}>
                        ×
                      </RemoveFileBtn>
                    </ImagePreview>
                  ) : (
                    <div
                      key={m.url}
                      style={{
                        position: "relative",
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      <DocChip>
                        <FileText size={12} />
                        {m.file.name}
                      </DocChip>
                      <RemoveFileBtn
                        style={{
                          position: "static",
                          marginLeft: "0.25rem",
                          background: "var(--secondary)",
                          color: "var(--foreground)",
                        }}
                        onClick={() => removeMedia(i)}
                      >
                        ×
                      </RemoveFileBtn>
                    </div>
                  ),
                )}
              </MediaPreviewGrid>
            )}

            {/* Location tag */}
            {locationTag && (
              <LocationTag onClick={handleLocation}>
                <MapPin size={14} />
                {locationTag}
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--muted-foreground)",
                    marginLeft: "0.25rem",
                  }}
                >
                  (remove)
                </span>
              </LocationTag>
            )}

            {content.length > MAX_CHARS * 0.8 && (
              <CharacterCount $warn={content.length >= MAX_CHARS}>
                {content.length} / {MAX_CHARS}
              </CharacterCount>
            )}
          </InputContainer>
        </ContentArea>

        {/* Hidden file inputs */}
        <input
          ref={imageInputRef}
          type="file"
          accept={IMAGE_TYPES.join(",")}
          multiple
          style={{ display: "none" }}
          onChange={handleImageSelect}
        />
        <input
          ref={docInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,text/plain"
          multiple
          style={{ display: "none" }}
          onChange={handleDocSelect}
        />

        {pollMode && <PollCreator onPollDataChange={setPollData} />}

        <Footer>
          {error && (
            <div style={{ color: "#ef4444", fontSize: "0.875rem" }}>
              {error}
            </div>
          )}
          <ToolsContainer>
            <ToolButton
              title="Add image"
              onClick={() => imageInputRef.current?.click()}
            >
              <ImageIcon size={16} />
            </ToolButton>
            <ToolButton
              title="Attach document"
              onClick={() => docInputRef.current?.click()}
            >
              <FileText size={16} />
            </ToolButton>
            <ToolButton
              title={checklistMode ? "Exit checklist mode" : "Checklist mode"}
              $active={checklistMode}
              onClick={handleChecklistToggle}
            >
              <ListChecks size={16} />
            </ToolButton>
            <ToolButton
              title={locationTag ? "Remove location" : "Add location"}
              $active={!!locationTag}
              onClick={handleLocation}
              disabled={locLoading}
            >
              {locLoading ? (
                <Loader
                  size={16}
                  style={{ animation: "spin 1s linear infinite" }}
                />
              ) : (
                <MapPin size={16} />
              )}
            </ToolButton>
            <ToolButton
              title={pollMode ? "Remove poll" : "Add poll"}
              $active={pollMode}
              onClick={handlePollToggle}
            >
              <BarChart2 size={16} />
            </ToolButton>
          </ToolsContainer>

          <ActionsContainer>
            <Tip>Ctrl+Enter to post</Tip>
            <ButtonGroup>
              <Button
                $variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  (!content.trim() &&
                    mediaFiles.length === 0 &&
                    validPollOptions.length < 2) ||
                  isSubmitting
                }
              >
                {isSubmitting && (
                  <Loader
                    size={14}
                    style={{ animation: "spin 1s linear infinite" }}
                  />
                )}
                {isSubmitting ? "Posting…" : "Post"}
              </Button>
            </ButtonGroup>
          </ActionsContainer>
        </Footer>
      </Modal>
    </Overlay>
  );
}

const TopicSelector = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TopicSelectorLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--muted-foreground);
`;

const TopicChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`;

const TopicChip = styled.button`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid ${(p) => (p.$selected ? "var(--primary)" : "var(--border)")};
  background: ${(p) =>
    p.$selected ? "rgba(var(--primary-rgb), 0.15)" : "transparent"};
  color: ${(p) => (p.$selected ? "var(--primary)" : "var(--foreground)")};

  &:hover {
    border-color: var(--primary);
  }
`;
